import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function useDriverConversations({ companyId, conversationIds }) {
  const inflightRef = useRef({});

  const ids = useMemo(() => Array.from(new Set((conversationIds ?? []).filter(Boolean))), [conversationIds]);

  const [transcripts, setTranscripts] = useState({});
  const [audio, setAudio] = useState({});
  const [loadingTranscripts, setLoadingTranscripts] = useState(true);

  useEffect(() => {
    setLoadingTranscripts(true);
    if (!ids.length) {
      setTranscripts({});
      setAudio((prev) => {
        Object.values(prev).forEach((a) => a.url && URL.revokeObjectURL(a.url));
        return {};
      });
      setLoadingTranscripts(false);
      return;
    }

    setTranscripts({});

    const aborters = ids.map(() => new AbortController());

    (async () => {
      const results = await Promise.allSettled(
        ids.map((conversationId, i) =>
          fetch('/api/elevenlabs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ conversationId, companyId }),
            signal: aborters[i].signal,
          }).then((r) => {
            if (!r.ok) throw new Error(`Transcript fetch failed: ${r.status}`);
            return r.json();
          })
        )
      );

      const next = {};
      results.forEach((res, idx) => {
        const id = ids[idx];
        if (res.status === 'fulfilled') {
          const data = res.value ?? {};
          next[id] = {
            transcriptSummary: data?.analysis?.transcript_summary ?? '',
            transcriptConversation: data?.transcript ?? [],
            transcriptStatus: data?.status ?? '',
            transcriptDuration: data?.metadata?.call_duration_secs ?? 0,
            transcriptStartTime: data?.metadata?.start_time_unix_secs ?? 0,
            transcriptDirection: data?.metadata?.phone_call?.direction ?? '',
          };
        } else {
          next[id] = {
            transcriptSummary: '',
            transcriptConversation: [],
            transcriptStatus: '',
            transcriptDuration: 0,
            transcriptStartTime: '',
          };
        }
      });

      setTranscripts(next);

      setAudio((prev) => {
        const updated = {};
        ids.forEach((id) => {
          updated[id] = prev[id] ?? { url: null, loading: false, error: null };
        });
        Object.entries(prev).forEach(([id, v]) => {
          if (!ids.includes(id) && v.url) URL.revokeObjectURL(v.url);
        });
        return updated;
      });

      setLoadingTranscripts(false);
    })();

    return () => aborters.forEach((a) => a.abort());
  }, [ids]);

  const loadAudio = useCallback(async (conversationId) => {
    if (!conversationId) return;

    if (inflightRef.current[conversationId]?.signal && !inflightRef.current[conversationId].signal.aborted) {
      return;
    }

    // Mark UI as loading (idempotent)
    setAudio((prev) => {
      const node = prev[conversationId] ?? { url: null, loading: false, error: null };
      return { ...prev, [conversationId]: { ...node, loading: true, error: null } };
    });

    // Start a fresh controller and cancel any previous one for this id
    inflightRef.current[conversationId]?.abort?.();
    const ctrl = new AbortController();
    inflightRef.current[conversationId] = ctrl;

    try {
      const res = await fetch(
        `/api/audio?conversationId=${encodeURIComponent(conversationId)}&companyId=${encodeURIComponent(
          companyId ?? ''
        )}`,
        { signal: ctrl.signal }
      );
      if (!res.ok) throw new Error(`Audio fetch failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      setAudio((prev) => {
        const prevUrl = prev[conversationId]?.url;
        if (prevUrl) URL.revokeObjectURL(prevUrl);
        return { ...prev, [conversationId]: { url, loading: false, error: null } };
      });
    } catch (e) {
      if (e.name !== 'AbortError') {
        setAudio((prev) => ({
          ...prev,
          [conversationId]: { url: null, loading: false, error: 'Could not load recording.' },
        }));
      }
    } finally {
      if (inflightRef.current[conversationId] === ctrl) {
        delete inflightRef.current[conversationId];
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      setAudio((prev) => {
        Object.values(prev).forEach((a) => a.url && URL.revokeObjectURL(a.url));
        return prev;
      });
    };
  }, []);

  const combinedTranscript = useMemo(() => {
    const items = ids
      .map((id) => ({ id, t: transcripts[id] }))
      .filter((x) => x.t)
      .sort((a, b) => Number(a.t.transcriptStartTime) - Number(b.t.transcriptStartTime));
    return items.flatMap((x) => x.t.transcriptConversation);
  }, [ids, transcripts]);

  return {
    ids,
    transcripts,
    audio,
    loadingTranscripts,
    loadAudio,
    combinedTranscript,
  };
}
